import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  PhotoLibrary,
  ViewInAr,
  Extension,
  Settings,
  Info,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useTheme, useMediaQuery } from '@mui/material';

const PluginsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [plugins, setPlugins] = useState([]);
  const [userPlugins, setUserPlugins] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Load available plugins and user's active plugins
  useEffect(() => {
    loadPlugins();
    loadUserPlugins();
  }, []);

  const loadPlugins = async () => {
    try {
      const response = await api.get('/plugins/available');
      if (response.data.success) {
        const available = response.data.data || [];
        // If no plugins at all, run initializer then retry
        if (available.length === 0) {
          // Attempt to initialize default plugins, then reload
          try {
            await api.post('/plugins/initialize');
            const retry = await api.get('/plugins/available');
            if (retry.data.success) {
              setPlugins(retry.data.data || []);
              return;
            }
          } catch (e) {
            // ignore, will fall through to set empty
          }
        }
        // If plugins exist but DymenWeb is missing, attempt on-the-fly initialization/upsert
        const dymenWeb = available.find(p => p.id === 'dymenweb');
        if (!dymenWeb || (Array.isArray(dymenWeb.menuItems) && dymenWeb.menuItems[0]?.label !== 'DymenWeb+')) {
          try {
            await api.post('/plugins/initialize');
            const retry = await api.get('/plugins/available');
            if (retry.data.success) {
              setPlugins(retry.data.data || []);
              return;
            }
          } catch (e) {
            // ignore and proceed to set current available list
          }
        }
        setPlugins(available);
      }
    } catch (error) {
      console.error('Failed to load plugins:', error);
      setError('Failed to load available plugins');
    }
  };

  const loadUserPlugins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/plugins/user');
      if (response.data.success) {
        const userPluginsMap = {};
        response.data.data.forEach(plugin => {
          userPluginsMap[plugin.pluginId] = plugin;
        });
        setUserPlugins(userPluginsMap);
      }
    } catch (error) {
      console.error('Failed to load user plugins:', error);
      setError('Failed to load your plugins');
    } finally {
      setLoading(false);
    }
  };

  const handlePluginToggle = async (pluginId) => {
    try {
      setActionLoading(prev => ({ ...prev, [pluginId]: true }));
      
      const isActive = userPlugins[pluginId]?.isActive;
      const endpoint = isActive ? 'deactivate' : 'activate';
      
      const response = await api.post(`/plugins/${pluginId}/${endpoint}`);
      
      if (response.data.success) {
        await loadUserPlugins(); // Reload to get updated state
        // Notify layout to refresh sidebar plugin items immediately
        window.dispatchEvent(new Event('plugins:updated'));
        setSuccessMessage(`Plugin ${isActive ? 'deactivated' : 'activated'} successfully!`);
      }
    } catch (error) {
      console.error(`Failed to toggle plugin:`, error);
      setError(`Failed to ${userPlugins[pluginId]?.isActive ? 'deactivate' : 'activate'} plugin`);
    } finally {
      setActionLoading(prev => ({ ...prev, [pluginId]: false }));
    }
  };

  const handleViewDetails = (plugin) => {
    setSelectedPlugin(plugin);
    setDetailsOpen(true);
  };

  const getPluginIcon = (pluginId) => {
    switch (pluginId) {
      case 'photo-adds':
        return <PhotoLibrary />;
      case 'storefront-3d':
        return <ViewInAr />;
      default:
        return <Extension />;
    }
  };

  // Normalize display names for plugins
  const getPluginDisplayName = (plugin) => {
    if (!plugin) return '';
    if (plugin.id === 'photo-adds') return 'Photo+ Ads';
    if (plugin.id === 'storefront-3d') return 'Storefront 3D';
    return plugin.name || '';
  };

  const getPluginStatus = (plugin) => {
    const userPlugin = userPlugins[plugin.id];
    if (!userPlugin) {
      return { label: 'Not Installed', color: 'default', icon: null };
    }
    if (userPlugin.isActive) {
      return { label: 'Active', color: 'success', icon: <CheckCircle /> };
    }
    return { label: 'Inactive', color: 'warning', icon: <Cancel /> };
  };

  return (
    <Box>
      <Snackbar open={!!successMessage} autoHideDuration={5000} onClose={() => setSuccessMessage('')}>
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Typography variant="h4" gutterBottom>
        Plugins
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Extend DymenStock's functionality with powerful plugins. Activate plugins to add new features and capabilities to your dashboard.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {plugins.map((plugin) => {
            const status = getPluginStatus(plugin);
            const isLoading = actionLoading[plugin.id];
            const userPlugin = userPlugins[plugin.id];
            
            return (
              <Grid item xs={12} sm={6} md={4} key={plugin.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {getPluginIcon(plugin.id)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="div">
                          {getPluginDisplayName(plugin)}
                        </Typography>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          icon={status.icon}
                        />
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plugin.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip label={`v${plugin.version}`} size="small" variant="outlined" />
                      <Chip label={plugin.category} size="small" variant="outlined" />
                    </Box>
                    
                    {plugin.features && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Key Features:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {plugin.features.slice(0, 2).join(', ')}
                          {plugin.features.length > 2 && '...'}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Info />}
                        onClick={() => handleViewDetails(plugin)}
                      >
                        Details
                      </Button>
                      {userPlugin?.isActive && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const menuPath = (plugin.menuItems && plugin.menuItems[0]?.path) || (plugin.entryPoint ? `/dashboard${plugin.entryPoint}` : null);
                            if (menuPath) navigate(menuPath);
                          }}
                        >
                          Open
                        </Button>
                      )}
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPlugin?.isActive || false}
                          onChange={() => handlePluginToggle(plugin.id)}
                          disabled={isLoading}
                        />
                      }
                      label={isLoading ? <CircularProgress size={16} /> : 'Active'}
                    />
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Plugin Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        {selectedPlugin && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {getPluginIcon(selectedPlugin.id)}
              </Avatar>
              <Box>
                <Typography variant="h6">{getPluginDisplayName(selectedPlugin)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Version {selectedPlugin.version} • {selectedPlugin.category}
                </Typography>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedPlugin.description}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Features
              </Typography>
              {selectedPlugin.features && (
                <Box component="ul" sx={{ pl: 2 }}>
                  {selectedPlugin.features.map((feature, index) => (
                    <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5 }}>
                      {feature}
                    </Typography>
                  ))}
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Requirements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • DymenStock v1.0+
                • Active subscription
                • {selectedPlugin.requirements || 'No additional requirements'}
              </Typography>
              
              {selectedPlugin.permissions && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Permissions
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {selectedPlugin.permissions.map((permission, index) => (
                      <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5 }}>
                        {permission}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handlePluginToggle(selectedPlugin.id);
                  setDetailsOpen(false);
                }}
                disabled={actionLoading[selectedPlugin.id]}
              >
                {userPlugins[selectedPlugin.id]?.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PluginsPage;
